import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Wand2, ArrowLeft, Sparkles, Save, Youtube, AlertTriangle, Eye, ListTree, Search, Mic, Clock, Music4, ChevronRight, ChevronDown, Loader2, X, Bot, Play, FolderPlus, ListMusic, Maximize2, Minus, Plus, RotateCcw, EyeOff, Type, Share2, Star, Check } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { sanitizeChordText, transposeKey, preferFlatsFor } from "@/lib/chords";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import StructureEditor from "@/components/StructureEditor";
import ChordSheetViewer from "@/components/ChordSheetViewer";
import StageLite from "@/components/StageLite";

const HISTORY_KEY = "michamou_web_history";
const loadHistory = () => { try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]"); } catch { return []; } };
const saveHistory = (arr) => { try { localStorage.setItem(HISTORY_KEY, JSON.stringify(arr.slice(0, 10))); } catch {} };
const POPULAR = ["Péricles", "Jorge & Mateus", "Marília Mendonça", "Fundo de Quintal", "Djavan", "Legião Urbana", "Tim Maia", "Bruno & Marrone"];

export default function FormatSong() {
  const navigate = useNavigate();
  const [step, setStep] = useState("input");

  // web search
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [history, setHistory] = useState(loadHistory());
  const [showHistory, setShowHistory] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const reqRef = useRef(0);
  const recRef = useRef(null);

  // song data / review
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [key, setKey] = useState("");
  const [category, setCategory] = useState("");
  const [useAi, setUseAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sections, setSections] = useState([]);
  const [needsReview, setNeedsReview] = useState(false);
  const [aiUsed, setAiUsed] = useState(false);
  const [aiGenerated, setAiGenerated] = useState(false);

  // extras (review)
  const [ytVideo, setYtVideo] = useState(null);
  const [showYt, setShowYt] = useState(false);
  const [ytLoading, setYtLoading] = useState(false);
  const [setlists, setSetlists] = useState([]);
  const [setlistOpen, setSetlistOpen] = useState(false);
  const [checkedSets, setCheckedSets] = useState({});
  const [newSetlistName, setNewSetlistName] = useState("");
  const [favorite, setFavorite] = useState(false);
  const [savedSong, setSavedSong] = useState(null);
  const [savingSet, setSavingSet] = useState(false);
  const [stagePreview, setStagePreview] = useState(false);
  const [steps, setSteps] = useState(0);
  const [hideChords, setHideChords] = useState(false);
  const [fontPx, setFontPx] = useState(15);
  const [showFont, setShowFont] = useState(false);

  // advanced: youtube + manual
  const [ytUrl, setYtUrl] = useState("");
  const [identified, setIdentified] = useState(null);
  const [ident, setIdent] = useState(false);

  const inp = "bg-background border-border rounded-md";
  const speechOK = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return; }
    setSearching(true);
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/music/search", { params: { q } });
        if (id === reqRef.current) setResults(data.results || []);
      } catch { if (id === reqRef.current) setResults([]); }
      finally { if (id === reqRef.current) setSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query]);

  const pushHistory = (item) => {
    const entry = { title: item.title, artist: item.artist, url: item.url };
    const next = [entry, ...history.filter((h) => h.url !== entry.url)];
    setHistory(next); saveHistory(next);
  };
  const clearHistory = () => { setHistory([]); saveHistory([]); setShowHistory(false); };

  const loadExtras = async (t, a) => {
    setYtVideo(null); setShowYt(false); setYtLoading(false); setCheckedSets({}); setNewSetlistName(""); setFavorite(false); setSavedSong(null);
    try { const { data } = await api.get("/repertoires"); setSetlists(data || []); } catch {}
  };

  // YouTube sob demanda: só consulta/carrega quando o usuário clica no botão discreto
  const toggleYoutube = async () => {
    if (ytVideo) { setShowYt((v) => !v); return; }
    if (ytLoading) return;
    const q = [artist, title].filter(Boolean).join(" ").trim();
    if (!q) return;
    setYtLoading(true);
    try {
      const { data } = await api.get("/music/youtube", { params: { q } });
      if (data?.video_id) { setYtVideo(data); setShowYt(true); }
      else toast.error("Nenhum vídeo encontrado no YouTube.");
    } catch { toast.error("Não foi possível buscar no YouTube."); }
    finally { setYtLoading(false); }
  };

  const importCifra = async (item) => {
    if (item.type === "artist") { setQuery(item.title); setShowHistory(false); return; }
    setImportingId(item.url);
    setShowHistory(false);
    pushHistory(item);
    try {
      const { data } = await api.post("/music/import", { title: item.title, artist: item.artist, url: item.url });
      const t = data.music || item.title, a = data.artist || item.artist;
      setTitle(t); setArtist(a); setKey(data.original_key || "");
      setText(data.raw_text || "");
      setSections((data.sections || []).map((s) => ({ notes: "", ...s })));
      setNeedsReview(!!data.needs_review); setAiUsed(!!data.ai_assisted); setAiGenerated(!!data.ai_generated);
      setStep("review");
      loadExtras(t, a);
      if (data.ai_generated) toast.warning("Cifra gerada por IA — revise com atenção antes de salvar.");
      else toast.success("Cifra importada! Revise a estrutura antes de salvar.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setImportingId(null); }
  };

  const startVoice = () => {
    if (!speechOK) return toast.error("Busca por voz não suportada neste navegador.");
    if (listening) { recRef.current?.stop(); return; }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "pt-BR"; rec.interimResults = false; rec.maxAlternatives = 1;
    rec.onresult = (e) => setQuery(e.results[0][0].transcript);
    rec.onerror = () => { setListening(false); toast.error("Não consegui ouvir. Tente novamente."); };
    rec.onend = () => setListening(false);
    recRef.current = rec; setListening(true); setShowHistory(false); rec.start();
  };

  const identify = async () => {
    if (!ytUrl.trim()) return toast.error("Cole o link do YouTube.");
    setIdent(true);
    try {
      const { data } = await api.get("/youtube/identify", { params: { url: ytUrl } });
      setIdentified(data);
      if (data.music) { setTitle(data.music); setQuery([data.artist, data.music].filter(Boolean).join(" ")); }
      if (data.artist) setArtist(data.artist);
      toast.success("Música identificada! Veja os resultados na busca acima.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setIdent(false); }
  };

  const onPasteText = (e) => {
    const clip = (e.clipboardData || window.clipboardData)?.getData("text");
    if (clip == null) return;
    e.preventDefault();
    const cleaned = sanitizeChordText(clip);
    const el = e.target;
    const start = el.selectionStart ?? text.length, end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + cleaned + text.slice(end));
  };

  const doFormat = async () => {
    if (!text.trim()) return toast.error("Cole a letra/cifra da música.");
    if (!title.trim()) return toast.error("Informe o nome da música.");
    setBusy(true);
    try {
      const { data } = await api.post("/songs/format", { text, title, artist, key, use_ai: useAi, source: "user_content" });
      setSections((data.sections || []).map((s) => ({ notes: "", ...s })));
      setNeedsReview(!!data.needs_review); setAiUsed(!!data.ai_assisted); setAiGenerated(false);
      setKey(data.original_key || key);
      setStep("review");
      loadExtras(title, artist);
      toast.success("Cifra formatada! Revise a estrutura antes de salvar.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const strip = (song) => ({ title: song.title, artist: song.artist || "", key: song.key || "", lyrics: song.lyrics || "", notes: song.notes || "", song_id: song.id, structured: song.structured });
  const buildBody = (fav) => ({ title: title.trim(), artist: artist.trim(), key: key.trim(), category: category.trim(), lyrics: text, structured: { original_key: key || "", current_key: key || "", sections, source: aiGenerated ? "ai" : "web" }, favorite: fav !== undefined ? fav : favorite });

  // cria a música na biblioteca só uma vez; reutiliza nas próximas ações
  const ensureSaved = async (fav) => {
    if (savedSong) return savedSong;
    const { data } = await api.post("/songs", buildBody(fav));
    setSavedSong(data);
    return data;
  };

  // coração: adiciona à biblioteca no 1º clique; depois alterna favorito
  const toggleFavorite = async () => {
    try {
      if (!savedSong) {
        setFavorite(true);
        const { data } = await api.post("/songs", buildBody(true));
        setSavedSong(data);
        toast.success("Adicionada à biblioteca!");
      } else {
        const next = !favorite;
        setFavorite(next);
        await api.patch(`/songs/${savedSong.id}/favorite`, { favorite: next });
        toast.success(next ? "Adicionada à biblioteca!" : "Removida da biblioteca.");
      }
    } catch (e) { setFavorite((v) => !v); toast.error(apiError(e.response?.data?.detail)); }
  };

  const openSetlist = () => { setCheckedSets({}); setNewSetlistName(""); setSetlistOpen(true); };

  // modal: grava a música no(s) setlist(s) escolhido(s) e/ou cria um novo já com a música
  const saveSetlist = async () => {
    const ids = Object.keys(checkedSets).filter((k) => checkedSets[k]);
    if (!ids.length && !newSetlistName.trim()) return toast.error("Escolha um setlist ou crie um novo.");
    setSavingSet(true);
    try {
      const song = await ensureSaved();
      for (const rid of ids) await api.post(`/repertoires/${rid}/songs`, strip(song));
      if (newSetlistName.trim()) await api.post("/repertoires", { name: newSetlistName.trim(), songs: [strip(song)] });
      toast.success("Adicionada ao setlist!");
      setSetlistOpen(false);
      try { const { data } = await api.get("/repertoires"); setSetlists(data || []); } catch {}
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingSet(false); }
  };

  const save = async () => {
    if (!sections.length) return toast.error("Nada para salvar.");
    setBusy(true);
    try {
      if (savedSong) await api.put(`/songs/${savedSong.id}`, buildBody());
      else await api.post("/songs", buildBody());
      toast.success("Música adicionada ao repertório!");
      navigate("/repertorio");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const preview = { original_key: key, current_key: key, sections };
  const baseKey = key || "";
  const curKey = transposeKey(baseKey, steps, preferFlatsFor(transposeKey(baseKey, steps, false)));
  const sharePdfFs = async () => {
    try {
      const res = await api.post("/songs/sheet.pdf", { title, artist, structured: preview }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a");
      a.href = url; a.download = `${(title || "cifra").replace(/\s+/g, "_").slice(0, 40) || "cifra"}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível gerar o PDF."); }
  };
  const showResults = query.trim().length >= 2;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="format-song-page">
      <button onClick={() => (step === "review" ? setStep("input") : navigate("/repertorio"))} className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="fs-back">
        <ArrowLeft className="h-4 w-4" />{step === "review" ? "Voltar à busca" : "Voltar ao repertório"}
      </button>

      {step === "input" && (
        <div className="space-y-6" data-testid="fs-input-step">
          <div className="text-center space-y-2 pt-2">
            <div className="inline-flex items-center justify-center h-12 w-12 rounded-2xl bg-accent/15 text-accent mb-1"><Search className="h-6 w-6" /></div>
            <h1 className="font-head text-3xl sm:text-4xl font-extrabold tracking-tight">Buscar na <span className="bg-gradient-to-r from-primary to-amber-400 bg-clip-text text-transparent">Web</span></h1>
          </div>

          <div className="relative">
            <div className="flex items-stretch gap-2.5">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
                <input
                  data-testid="fs-web-search-input"
                  value={query}
                  onChange={(e) => { setQuery(e.target.value); setShowHistory(false); }}
                  onKeyDown={(e) => { if (e.key === "Escape") setQuery(""); }}
                  placeholder="Qual música você quer tocar hoje?"
                  aria-label="Buscar música, artista ou álbum"
                  autoFocus
                  className="w-full h-14 pl-12 pr-24 rounded-2xl bg-surface border border-border text-base shadow-sm outline-none transition-colors focus:border-accent focus:ring-2 focus:ring-accent/30 placeholder:text-muted-foreground"
                />
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {query && <button data-testid="fs-web-clear" onClick={() => setQuery("")} aria-label="Limpar busca" className="h-9 w-9 grid place-items-center rounded-full text-muted-foreground hover:bg-surface-hover transition-colors"><X className="h-4 w-4" /></button>}
                  {speechOK && (
                    <button data-testid="fs-web-mic" onClick={startVoice} aria-label="Buscar por voz" aria-pressed={listening}
                      className={`h-9 w-9 grid place-items-center rounded-full transition-colors ${listening ? "bg-primary text-primary-foreground animate-pulse" : "text-accent hover:bg-accent/10"}`}><Mic className="h-5 w-5" /></button>
                  )}
                </div>
              </div>
              <button data-testid="fs-history-btn" onClick={() => setShowHistory((v) => !v)} aria-label="Histórico de buscas" aria-expanded={showHistory}
                className={`h-14 w-14 shrink-0 grid place-items-center rounded-2xl border transition-colors ${showHistory ? "bg-accent text-accent-foreground border-accent" : "bg-surface border-border text-foreground hover:border-accent/50"}`}>
                <Clock className="h-5 w-5" />
              </button>
            </div>

            {showHistory && (
              <div data-testid="fs-history-panel" className="absolute z-20 mt-2 w-full rounded-2xl border border-border bg-surface shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
                  <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Buscas recentes</span>
                  {history.length > 0 && <button data-testid="fs-history-clear" onClick={clearHistory} className="text-xs text-muted-foreground hover:text-destructive transition-colors">Limpar</button>}
                </div>
                {history.length === 0 ? <div className="px-4 py-6 text-sm text-muted-foreground text-center">Nenhuma busca recente ainda.</div> : (
                  <ul className="max-h-72 overflow-y-auto divide-y divide-border">
                    {history.map((h, i) => (
                      <li key={i}>
                        <button data-testid={`fs-history-item-${i}`} onClick={() => importCifra({ ...h, type: "song" })} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors">
                          <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                          <span className="min-w-0 flex-1"><span className="block font-medium truncate">{h.title}</span><span className="block text-xs text-muted-foreground truncate">{h.artist}</span></span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {!showResults ? (
            <div className="space-y-5">
              <p className="text-center text-sm text-muted-foreground max-w-md mx-auto leading-relaxed" data-testid="fs-web-helper">
                Digite o nome da música, artista ou álbum. Se a cifra não existir, a <span className="text-accent font-medium">nossa IA busca para você</span>!
              </p>
              <div className="flex flex-wrap gap-2 justify-center max-w-xl mx-auto" data-testid="fs-suggestions">
                {POPULAR.map((p) => (
                  <button key={p} data-testid={`fs-suggestion-${p}`} onClick={() => setQuery(p)}
                    className="px-3.5 py-1.5 rounded-full border border-border bg-surface text-sm text-muted-foreground hover:text-accent hover:border-accent/50 transition-colors">{p}</button>
                ))}
              </div>
            </div>
          ) : (
            <div data-testid="fs-web-results" className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground px-1">
                {searching ? <><Loader2 className="h-4 w-4 animate-spin" />Buscando…</> : <span>Resultados{results.length ? ` (${results.length})` : ""}</span>}
              </div>
              {!searching && results.length === 0 && (
                <div className="rounded-2xl border border-dashed border-border bg-surface p-6 text-center space-y-3">
                  <p className="text-sm text-muted-foreground">Nenhuma cifra encontrada para “{query}”.</p>
                  <Button data-testid="fs-ai-generate" onClick={() => importCifra({ type: "song", title: query.trim(), artist: "", url: "" })} className="rounded-xl gap-2"><Bot className="h-4 w-4" />Gerar com IA</Button>
                </div>
              )}
              <ul className="space-y-2">
                {results.map((r) => {
                  const loading = importingId === r.url;
                  return (
                    <li key={`${r.type}-${r.id}`}>
                      <button data-testid={`fs-result-${r.type}-${r.id}`} onClick={() => importCifra(r)} disabled={!!importingId}
                        className="group w-full flex items-center gap-3 rounded-2xl border border-border bg-surface px-4 py-3 text-left transition-all hover:border-accent/50 hover:bg-accent/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-60">
                        <span className="grid h-10 w-10 place-items-center rounded-xl bg-accent/12 text-accent shrink-0"><Music4 className="h-5 w-5" /></span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-head font-semibold truncate">{r.title}</span>
                          <span className="block text-xs text-muted-foreground truncate">{r.artist}{r.type === "artist" ? " · artista" : ""}</span>
                        </span>
                        {loading ? <Loader2 className="h-5 w-5 animate-spin text-accent shrink-0" /> : <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:text-accent" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          <div className="pt-2">
            <button data-testid="fs-adv-toggle" onClick={() => setAdvOpen((v) => !v)} aria-expanded={advOpen} className="mx-auto flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
              <ChevronDown className={`h-4 w-4 transition-transform ${advOpen ? "rotate-180" : ""}`} />Outras formas de importar (colar cifra ou YouTube)
            </button>
            {advOpen && (
              <div className="mt-4 space-y-4" data-testid="fs-advanced">
                <div className="bg-surface border border-border rounded-2xl p-5 space-y-3">
                  <h3 className="font-head text-sm font-semibold flex items-center gap-2"><Youtube className="h-4 w-4 text-primary" />Identificar por link do YouTube</h3>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input data-testid="fs-yt-url" value={ytUrl} onChange={(e) => setYtUrl(e.target.value)} placeholder="https://youtube.com/watch?v=..." className={inp} />
                    <Button data-testid="fs-yt-identify" onClick={identify} disabled={ident} variant="outline" className="rounded-md gap-2 shrink-0 bg-transparent"><Youtube className="h-4 w-4" />{ident ? "Consultando..." : "Identificar"}</Button>
                  </div>
                  {identified && <p className="text-xs text-muted-foreground">Encontrado: <b className="text-foreground">{identified.music}</b>{identified.artist ? ` · ${identified.artist}` : ""} — veja os resultados na busca acima.</p>}
                </div>
                <div className="bg-surface border border-border rounded-2xl p-5 space-y-3">
                  <h3 className="font-head text-sm font-semibold flex items-center gap-2"><Wand2 className="h-4 w-4 text-accent" />Colar cifra e formatar</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <Input data-testid="fs-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Música *" className={inp} />
                    <Input data-testid="fs-artist" value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="Artista" className={inp} />
                    <Input data-testid="fs-key" value={key} onChange={(e) => setKey(e.target.value)} placeholder="Tom (ex: G)" className={inp} />
                  </div>
                  <Textarea data-testid="fs-text" value={text} onChange={(e) => setText(e.target.value)} onPaste={onPasteText} className={`${inp} font-mono text-sm min-h-[200px]`} placeholder={"[Intro]\nG  D  Em  C\n\n[Verso]\nG           D\nEssa letra vai aqui..."} />
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="fs-use-ai-label">
                    <Checkbox data-testid="fs-use-ai" checked={useAi} onCheckedChange={(v) => setUseAi(!!v)} /><Sparkles className="h-4 w-4 text-accent" />Usar IA como apoio quando a estrutura não for clara
                  </label>
                  <Button data-testid="fs-format-btn" onClick={doFormat} disabled={busy} className="rounded-md gap-2"><Wand2 className="h-4 w-4" />{busy ? "Formatando..." : "Formatar (Mí Chamou)"}</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {step === "review" && (
        <div className="space-y-4" data-testid="fs-review-step">
          <div className="min-w-0">
            <h1 className="font-head text-2xl font-bold tracking-tight flex items-center gap-2"><Wand2 className="h-6 w-6 text-accent shrink-0" /><span className="truncate">{title || "Cifra"}</span></h1>
            {artist && <p className="text-sm text-muted-foreground">{artist}</p>}
          </div>
          <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-surface px-2 py-2 sticky top-0 z-10">
            <div className="flex items-center gap-1">
              <button data-testid="fs-transpose-down" onClick={() => setSteps((s) => s - 1)} aria-label="Descer meio tom" className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover"><Minus className="h-4 w-4" /></button>
              <div className="text-center leading-none px-1"><div className="text-[9px] uppercase tracking-wider text-muted-foreground">Tom</div><div className="font-mono font-bold text-accent text-sm" data-testid="fs-current-key">{curKey || "—"}</div></div>
              <button data-testid="fs-transpose-up" onClick={() => setSteps((s) => s + 1)} aria-label="Subir meio tom" className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-surface-hover"><Plus className="h-4 w-4" /></button>
              {steps !== 0 && <button data-testid="fs-transpose-reset" onClick={() => setSteps(0)} aria-label="Tom original" className="h-9 w-9 grid place-items-center rounded-lg text-muted-foreground hover:text-accent"><RotateCcw className="h-4 w-4" /></button>}
            </div>
            <div className="flex items-center gap-1.5">
              <button data-testid="fs-toggle-chords" onClick={() => setHideChords((v) => !v)} aria-pressed={hideChords} aria-label={hideChords ? "Mostrar cifras" : "Só letra (cantores)"} className={`h-9 w-9 grid place-items-center rounded-lg border transition-colors ${hideChords ? "bg-accent/15 border-accent/40 text-accent" : "bg-transparent border-border text-muted-foreground hover:text-foreground"}`}>{hideChords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
              <button data-testid="fs-font-toggle" onClick={() => setShowFont((v) => !v)} aria-pressed={showFont} aria-label="Tamanho da fonte" className={`h-9 w-9 grid place-items-center rounded-lg border transition-colors ${showFont ? "bg-accent/15 border-accent/40 text-accent" : "bg-transparent border-border text-muted-foreground hover:text-foreground"}`}><Type className="h-4 w-4" /></button>
              <button data-testid="cs-library-heart-btn" onClick={toggleFavorite} aria-pressed={favorite} aria-label={favorite ? "Remover da biblioteca" : "Adicionar à biblioteca"} title={favorite ? "Na biblioteca" : "Adicionar à biblioteca"} className={`h-9 w-9 grid place-items-center rounded-lg border transition-colors ${favorite ? "bg-accent/15 border-accent/40 text-accent" : "bg-transparent border-border text-muted-foreground hover:text-foreground"}`}><Star className={`h-4 w-4 ${favorite ? "fill-current" : ""}`} /></button>
              <button data-testid="cs-add-setlist-btn" onClick={openSetlist} aria-label="Adicionar a setlist" title="Adicionar a setlist" className="h-9 w-9 grid place-items-center rounded-lg border bg-transparent border-border text-muted-foreground hover:text-foreground transition-colors"><FolderPlus className="h-4 w-4" /></button>
              <button data-testid="fs-pdf" onClick={sharePdfFs} aria-label="Exportar PDF" className="h-9 w-9 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors"><Share2 className="h-4 w-4" /></button>
              <button data-testid="fs-stage-preview" onClick={() => setStagePreview(true)} disabled={!sections.length} aria-label="Modo Palco" className="h-9 px-3 inline-flex items-center gap-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-semibold disabled:opacity-50"><Maximize2 className="h-4 w-4" /><span className="hidden sm:inline">Modo Palco</span></button>
            </div>
          </div>
          {showFont && (
            <div className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-2" data-testid="fs-font-bar">
              <Type className="h-4 w-4 text-muted-foreground shrink-0" />
              <button data-testid="fs-font-dec" onClick={() => setFontPx((f) => Math.max(12, f - 1))} aria-label="Diminuir fonte" className="h-8 w-8 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-foreground"><Minus className="h-4 w-4" /></button>
              <input data-testid="fs-font-slider" type="range" min="12" max="28" value={fontPx} onChange={(e) => setFontPx(parseInt(e.target.value))} className="flex-1 h-2 accent-accent" aria-label="Ajustar tamanho da fonte" />
              <button data-testid="fs-font-inc" onClick={() => setFontPx((f) => Math.min(28, f + 1))} aria-label="Aumentar fonte" className="h-8 w-8 grid place-items-center rounded-lg border border-border text-muted-foreground hover:text-foreground"><Plus className="h-4 w-4" /></button>
              <span className="font-mono text-sm text-foreground w-10 text-right">{fontPx}px</span>
            </div>
          )}

          {aiGenerated && (
            <div className="flex items-start gap-2 rounded-md border border-info/40 bg-info/10 text-info px-3 py-2 text-sm" data-testid="fs-ai-generated-warning">
              <Bot className="h-4 w-4 mt-0.5 shrink-0" /><span>Esta cifra foi <b>gerada por IA</b> pois não encontramos na web. Revise acordes e letra com atenção antes de salvar.</span>
            </div>
          )}
          {needsReview && (
            <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 text-warning px-3 py-2 text-sm" data-testid="fs-review-warning">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>Estrutura não identificada com segurança — revise as seções antes de salvar. {aiUsed && "(IA ajudou na rotulagem)"}</span>
            </div>
          )}

          {/* Ouvir no YouTube — botão discreto, carrega só ao clicar */}
          <div className="rounded-2xl border border-border bg-surface overflow-hidden" data-testid="fs-yt-card">
            <button data-testid="fs-yt-toggle" onClick={toggleYoutube} aria-expanded={showYt} disabled={ytLoading} className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover transition-colors disabled:opacity-70">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary shrink-0"><Youtube className="h-4 w-4" /></span>
              <span className="min-w-0 flex-1"><span className="block font-head font-semibold text-sm">Ouvir no YouTube</span><span className="block text-xs text-muted-foreground truncate">{ytLoading ? "Buscando vídeo..." : ytVideo ? ytVideo.title : "Toque para buscar o vídeo da música"}</span></span>
              {ytLoading ? <Loader2 className="h-5 w-5 text-muted-foreground shrink-0 animate-spin" /> : ytVideo ? <ChevronDown className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${showYt ? "rotate-180" : ""}`} /> : <Play className="h-4 w-4 text-muted-foreground/60 shrink-0" />}
            </button>
            {showYt && ytVideo && (
              <div className="aspect-video w-full bg-black">
                <iframe data-testid="fs-yt-iframe" title="YouTube player" className="w-full h-full" src={`https://www.youtube-nocookie.com/embed/${ytVideo.video_id}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            )}
          </div>

          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="inline-flex h-auto gap-1 bg-transparent p-0 mb-1" data-testid="fs-tabs">
              <TabsTrigger value="preview" data-testid="fs-tab-preview" className="gap-1 rounded-full px-2.5 py-1 text-xs text-muted-foreground data-[state=active]:bg-accent/15 data-[state=active]:text-accent data-[state=active]:shadow-none"><Eye className="h-3.5 w-3.5" />Pré-visualizar</TabsTrigger>
              <TabsTrigger value="edit" data-testid="fs-tab-edit" className="gap-1 rounded-full px-2.5 py-1 text-xs text-muted-foreground data-[state=active]:bg-accent/15 data-[state=active]:text-accent data-[state=active]:shadow-none"><ListTree className="h-3.5 w-3.5" />Revisar estrutura</TabsTrigger>
            </TabsList>
            <TabsContent value="preview" className="mt-4">
              <div className="bg-surface border border-border rounded-lg p-4"><ChordSheetViewer structured={preview} music={title} artist={artist} showHeader={false} showNav={false} steps={steps} hideChords={hideChords} fontPx={fontPx} /></div>
            </TabsContent>
            <TabsContent value="edit" className="mt-4"><StructureEditor sections={sections} onChange={setSections} /></TabsContent>
          </Tabs>

          <div className="bg-surface border border-border rounded-lg p-4 space-y-4">
            <div className="flex flex-wrap items-end gap-3">
              <div><label className="text-xs text-muted-foreground">Categoria (opcional)</label><Input data-testid="fs-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Ex: Samba" className={`${inp} w-56`} /></div>
              {favorite && <span data-testid="fs-fav-badge" className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-accent/40 bg-accent/10 text-accent"><Star className="h-3 w-3 fill-current" />Na biblioteca</span>}
            </div>
            <Button data-testid="fs-save-btn" onClick={save} disabled={busy} className="rounded-md gap-2 bg-success text-success-foreground hover:opacity-90"><Save className="h-4 w-4" />{busy ? "Salvando..." : "Adicionar ao repertório"}</Button>
          </div>
          <StageLite open={stagePreview} songs={[{ title, artist, key, structured: preview }]} onClose={() => setStagePreview(false)} />

          {/* ===== Modal: Adicionar a setlist ===== */}
          <Dialog open={setlistOpen} onOpenChange={setSetlistOpen}>
            <DialogContent className="max-w-md" data-testid="cs-setlist-modal">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><FolderPlus className="h-5 w-5 text-accent" />Adicionar a setlist</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">A música será incluída no(s) setlist(s) escolhido(s) e salva na sua biblioteca.</p>
                {setlists.length > 0 ? (
                  <div className="max-h-56 overflow-y-auto space-y-1.5">
                    {setlists.map((g) => (
                      <label key={g.id} data-testid={`cs-setlist-item-${g.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-surface hover:border-accent/40 cursor-pointer transition-colors">
                        <Checkbox data-testid={`cs-setlist-check-${g.id}`} checked={!!checkedSets[g.id]} onCheckedChange={(v) => setCheckedSets((c) => ({ ...c, [g.id]: !!v }))} />
                        <span className="min-w-0 flex-1"><span className="block font-medium text-sm truncate">{g.name}</span><span className="block text-xs text-muted-foreground">{(g.songs || []).length} música(s)</span></span>
                      </label>
                    ))}
                  </div>
                ) : <p className="text-sm text-muted-foreground">Você ainda não tem setlists. Crie um abaixo.</p>}
                <div className="flex items-center gap-2 border-t border-border pt-3">
                  <Plus className="h-4 w-4 text-primary shrink-0" />
                  <Input data-testid="cs-create-setlist-name" value={newSetlistName} onChange={(e) => setNewSetlistName(e.target.value)} placeholder="Criar novo setlist (ex: Pagode 90)" className="bg-background border-border rounded-md" />
                </div>
                <Button data-testid="cs-setlist-save-confirm" onClick={saveSetlist} disabled={savingSet} className="w-full rounded-md gap-2">{savingSet ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}Salvar no setlist</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </div>
  );
}
